import { useMemo } from "react";
import { useQuery } from "convex/react";
import { useDeferredPublicEnhancement } from "@/hooks/use-deferred-public-enhancement";
import type { TFunction } from "i18next";
import { api } from "@/convex/_generated/api";
import type { PublicService } from "@/convex/crm/serviceCatalogShared";
import { SERVICE_KEYS, SERVICE_SLUGS, type ServiceKey } from "@/lib/routes";
import type { SupportedLocale } from "@/i18n-locales";
import { SERVICES } from "@/pages/services/_lib/services-data";
import { getServicePricing } from "@/pages/services/_lib/service-pricing-data";
import type {
  BillingMode,
  LocalizedText,
} from "@/convex/crm/serviceCatalogShared";
import { SERVICE_OFFER_COPY } from "@/convex/crm/serviceCatalogShared";

// ============================================================================
// Public services data layer (Fase 2, milestone 2)
//
// The public site reads services from the CRM catalog when it is populated, and
// falls back to the current STATIC content only after the authoritative query
// has completed and confirmed that the catalog is empty. Rendering the static
// list while the query is still pending would briefly expose obsolete titles
// after an editor changes a service in the CRM.
// ============================================================================

// Map the legacy static pricing model to the catalog's billing model, mirroring
// the backend seed exactly so display + checkout stay consistent.
function staticPricing(key: ServiceKey): {
  billingMode: BillingMode;
  oneTimeAmount?: number;
  recurringAmount?: number;
  recurringInterval?: string;
  priceFrom: boolean;
  publicPrice: boolean;
} {
  const pricing = getServicePricing(key);
  if (!pricing) {
    // Coworking / booking-only services: quote, no public price.
    return { billingMode: "quote", priceFrom: false, publicPrice: false };
  }
  if (pricing.mode === "one_time") {
    return {
      billingMode: "one_time",
      oneTimeAmount: pricing.setup,
      priceFrom: pricing.from ?? false,
      publicPrice: true,
    };
  }
  if (pricing.mode === "monthly") {
    return {
      billingMode: "subscription",
      recurringAmount: pricing.monthly,
      recurringInterval: "monthly",
      priceFrom: pricing.from ?? false,
      publicPrice: true,
    };
  }
  // one_time_plus_monthly -> hybrid
  return {
    billingMode: "hybrid",
    oneTimeAmount: pricing.setup,
    recurringAmount: pricing.monthly,
    recurringInterval: "monthly",
    priceFrom: pricing.from ?? false,
    publicPrice: true,
  };
}

function slugsOf(key: ServiceKey): LocalizedText {
  const s = SERVICE_SLUGS[key] as Record<SupportedLocale, string>;
  return { it: s.it, en: s.en, es: s.es, fr: s.fr, de: s.de };
}

// Build a single static PublicService from the i18n content for a service key.
function buildStaticService(
  key: ServiceKey,
  index: number,
  locale: string,
  t: TFunction,
): PublicService {
  const meta = SERVICES.find((s) => s.key === key);
  const pricing = staticPricing(key);
  const features = Array.from({ length: meta?.featureCount ?? 6 }, (_, i) =>
    t(`${key}.feature.${i + 1}`),
  ).filter((f) => f && f.length > 0);
  const title = t(`${key}.title`);
  const summary = t(`${key}.summary`);
  const slugs = slugsOf(key);
  const offerCopy =
    SERVICE_OFFER_COPY[locale as SupportedLocale] ?? SERVICE_OFFER_COPY.it;
  return {
    serviceKey: key,
    slug: slugs[locale as SupportedLocale] ?? slugs.it,
    slugs,
    icon: meta?.iconName ?? "Sparkles",
    gradient: meta?.gradient ?? "from-primary to-accent",
    bookingUrl: meta?.bookingUrl,
    imageUrl: undefined,
    title,
    tagline: t(`${key}.tagline`),
    summary,
    description: t(`${key}.intro`),
    description2: t(`${key}.intro2`),
    features,
    ctaLabel: t("detail.cta_button"),
    priceText: "",
    seoTitle: title ? `${title} — Enkey Agency` : "",
    metaDescription: summary,
    usedFallback: false,
    order: index,
    aiOffer: {
      variant: "ai",
      title: offerCopy.ai.title,
      description: offerCopy.ai.description,
      enabled: key !== "coworking",
      billingMode: "quote",
      currency: "EUR",
      vatIncluded: false,
      priceFrom: true,
      publicPrice: false,
    },
    proOffer: {
      variant: "pro",
      title: offerCopy.pro.title,
      description: offerCopy.pro.description,
      enabled: key !== "coworking",
      billingMode: "quote",
      currency: "EUR",
      vatIncluded: false,
      priceFrom: true,
      publicPrice: false,
    },
    currency: "EUR",
    vatIncluded: false,
    ...pricing,
  };
}

// The full static list, ordered like SERVICE_KEYS.
export function buildStaticServices(
  locale: string,
  t: TFunction,
): PublicService[] {
  return SERVICE_KEYS.map((key, i) => buildStaticService(key, i, locale, t));
}

// ----- Hooks ----------------------------------------------------------------

export type PublicServicesResult = {
  services: PublicService[];
  /** True while the authoritative catalog query is still loading. */
  isLoading: boolean;
  /** True when the static fallback is being used (catalog empty/unavailable). */
  usingFallback: boolean;
};

// List all services for the grid. Reads the catalog; falls back to static.
export function usePublicServices(
  locale: string,
  t: TFunction,
  options: { defer?: boolean; enabled?: boolean } = {},
): PublicServicesResult {
  const deferredReady = useDeferredPublicEnhancement();
  const queryReady =
    options.enabled !== false && (!options.defer || deferredReady);
  const fromDb = useQuery(
    api.crm.serviceCatalog.listPublic,
    queryReady ? { locale } : "skip",
  );
  const staticServices = useMemo(
    () => buildStaticServices(locale, t),
    [locale, t],
  );

  if (fromDb === undefined) {
    // Never render potentially stale built-in labels while the CRM catalogue is
    // loading. Consumers already expose skeletons or tolerate an empty list.
    return { services: [], isLoading: true, usingFallback: false };
  }
  if (fromDb.length === 0) {
    return { services: staticServices, isLoading: false, usingFallback: true };
  }
  return { services: fromDb, isLoading: false, usingFallback: false };
}

export type PublicServiceResult = {
  service: PublicService | null;
  isLoading: boolean;
  usingFallback: boolean;
};

// Resolve a single service by any localized slug. Reads the catalog; falls back
// to static content resolved from the slug.
export function usePublicService(
  slug: string | undefined,
  locale: string,
  t: TFunction,
): PublicServiceResult {
  const fromDb = useQuery(
    api.crm.serviceCatalog.getPublicBySlug,
    slug ? { slug, locale } : "skip",
  );

  // Resolve the static equivalent from the slug (any locale) for fallback.
  const staticService = useMemo(() => {
    if (!slug) return null;
    for (let i = 0; i < SERVICE_KEYS.length; i += 1) {
      const key = SERVICE_KEYS[i];
      const slugs = SERVICE_SLUGS[key];
      const match = Object.values(slugs).includes(slug) || key === slug;
      if (match) return buildStaticService(key, i, locale, t);
    }
    return null;
  }, [slug, locale, t]);

  if (!slug) return { service: null, isLoading: false, usingFallback: true };
  if (fromDb === undefined) {
    return { service: null, isLoading: true, usingFallback: false };
  }
  if (fromDb === null) {
    // Not in the catalog (or not published): use static if it exists.
    return { service: staticService, isLoading: false, usingFallback: true };
  }
  return { service: fromDb, isLoading: false, usingFallback: false };
}
